import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import {
  Box,
  Button,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Badge,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Select,
  MenuItem as SelectMenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as CloudUploadIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import api from '../../services/api';

// No mock data - using database data only

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, user, hasPermission } = useAuth();
  const { formatCurrency } = useSettings();
  const userRole = user?.role || '';
  const isShipmentManager = userRole === 'shipment_manager';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  
  // Import related states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'insert_only' | 'update_only' | 'upsert'>('upsert');
  const [showColumnInstructions, setShowColumnInstructions] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Stock count PDF state
  const [stockCountLoading, setStockCountLoading] = useState(false);
  const [stockCountError, setStockCountError] = useState<string | null>(null);

  // Export related states
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'xlsx'>('xlsx');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  
  // Enhanced export filter states
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [exportMode, setExportMode] = useState<'customer' | 'advanced'>('customer');
  const [exportFilters, setExportFilters] = useState({
    stockBoxOperation: 'all' as 'all' | 'less_than' | 'greater_than' | 'equal_to',
    stockBoxValue: 0,
    stockSingleOperation: 'all' as 'all' | 'less_than' | 'greater_than' | 'equal_to',
    stockSingleValue: 0,
    priceMin: '',
    priceMax: '',
    activeStatus: 'all' as 'all' | 'active' | 'inactive',
    dateFrom: '',
    dateTo: ''
  });
  
  // Image preview state
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<{url: string, name: string} | null>(null);

  // Function to fetch products and categories data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch products from the backend API
      const productsResponse = await api.get('/products');
      if (productsResponse.data && productsResponse.data.success) {
        // Transform the data to match our frontend structure
        const transformedProducts = productsResponse.data.data.map((product: any) => ({
          id: product.id,
          sku: product.sku,
          name: product.name,
          category: { id: product.category_id, name: product.category_name || 'Uncategorized' },
          image: product.image_url || getImageUrl(null),
          unit_size: product.unit_size || '',
          qtyInBox: product.qty_in_box || 1,
          unitType: product.unit_type || 'Piece',
          unitPrice: product.unit_price || 0,
          taxRate: product.tax_rate || 0,
          stockCount: product.stock_quantity || 0,
          stockSingle: product.stock_single || 0
        }));
        setProducts(transformedProducts);
      } else {
        // If API call succeeds but returns no data, set empty array
        console.warn('No product data received from API');
        setProducts([]);
      }
      
      // Fetch categories from the backend API
      try {
        const categoriesResponse = await api.get('/categories');
        if (categoriesResponse.data && categoriesResponse.data.success) {
          // Add 'All' category at the beginning
          const allCategories = [{ id: 0, name: 'All' }, ...categoriesResponse.data.data];
          setCategories(allCategories);
        } else {
          // Set empty array with just 'All' category
          console.warn('No category data received from API');
          setCategories([{ id: 0, name: 'All' }]);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([{ id: 0, name: 'All' }]);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      // Set empty arrays if API calls fail
      setProducts([]);
      setCategories([{ id: 0, name: 'All' }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch data when authentication is ready and user is authenticated
    if (!authLoading && isAuthenticated) {
      fetchData();
    }
  }, [authLoading, isAuthenticated]);

  const handleAddProduct = () => {
    navigate('/products/add');
  };

  const handleImagePreview = (imageUrl: string, productName: string) => {
    setPreviewImage({ url: imageUrl, name: productName });
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImage(null);
  };

  const handleEditProduct = (product: any) => {
    navigate(`/products/edit/${product.id}`);
  };
  
  const handleImportClick = () => {
    // Reset import states
    setImportFile(null);
    setImportResult(null);
    setImportError(null);
    setImportMode('upsert'); // Reset to default mode
    setShowColumnInstructions(false);
    setImportDialogOpen(true);
  };
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setImportFile(event.target.files[0]);
      setImportError(null);
    }
  };
  
  const handleImportSubmit = async () => {
    if (!importFile) {
      setImportError('Please select a file to import');
      return;
    }
    
    // Validate file type
    const fileExtension = importFile.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(fileExtension || '')) {
      setImportError('Only CSV and Excel files (xlsx, xls) are supported');
      return;
    }
    
    setImportLoading(true);
    setImportError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('mode', importMode); // Add import mode to form data
      
      const response = await api.post('/products/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data && response.data.success) {
        setImportResult(response.data.data);
        // Refresh product list after successful import
        fetchData();
      } else {
        setImportError(response.data?.message || 'Import failed');
      }
    } catch (error: any) {
      console.error('Error importing products:', error);
      setImportError(error.response?.data?.message || 'Error importing products');
    } finally {
      setImportLoading(false);
    }
  };
  
  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
  };
  
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleStockCountPDF = async () => {
    try {
      setStockCountLoading(true);
      setStockCountError(null);

      const response = await api.get('/products/stock-count/pdf', {
        responseType: 'blob',
        timeout: 60000,
      });

      if (response.data.type !== 'application/pdf') {
        throw new Error('Invalid PDF response');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-count-sheet-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading stock count PDF:', error);
      setStockCountError('Error generating stock count PDF. Please try again.');
    } finally {
      setStockCountLoading(false);
    }
  };

  const handleExportClick = () => {
    // Reset export states
    setExportError(null);
    setShowAdvancedFilters(false);
    setExportMode('customer');
    setExportFilters({
      stockBoxOperation: 'all',
      stockBoxValue: 0,
      stockSingleOperation: 'all',
      stockSingleValue: 0,
      priceMin: '',
      priceMax: '',
      activeStatus: 'all',
      dateFrom: '',
      dateTo: ''
    });
    setExportDialogOpen(true);
  };
  
  const handleExportSubmit = async () => {
    setExportLoading(true);
    setExportError(null);
    
    try {
      const params = new URLSearchParams({
        format: exportFormat,
        export_mode: exportMode
      });
      
      // Add existing category filter (preserve existing functionality)
      if (selectedCategory > 0) {
        params.append('category_id', selectedCategory.toString());
      }
      
      // Advanced filters are kept in state when switching modes, so they must
      // only be sent in advanced mode or they silently filter the customer list
      if (exportMode === 'advanced') {
        if (exportFilters.stockBoxOperation !== 'all') {
          params.append('stock_box_operation', exportFilters.stockBoxOperation);
          params.append('stock_box_value', exportFilters.stockBoxValue.toString());
        }

        if (exportFilters.stockSingleOperation !== 'all') {
          params.append('stock_single_operation', exportFilters.stockSingleOperation);
          params.append('stock_single_value', exportFilters.stockSingleValue.toString());
        }

        if (exportFilters.priceMin !== '') {
          params.append('price_min', exportFilters.priceMin);
        }

        if (exportFilters.priceMax !== '') {
          params.append('price_max', exportFilters.priceMax);
        }

        if (exportFilters.activeStatus !== 'all') {
          params.append('is_active', exportFilters.activeStatus === 'active' ? 'true' : 'false');
        }

        if (exportFilters.dateFrom !== '') {
          params.append('date_from', exportFilters.dateFrom);
        }

        if (exportFilters.dateTo !== '') {
          params.append('date_to', exportFilters.dateTo);
        }
      }
      
      const response = await api.get(`/products/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and download
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `products_export.${exportFormat}`;
      if (contentDisposition) {
        console.log('Content-Disposition:', contentDisposition);
        // Try to extract filename from standard format first
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (!filenameMatch) {
          console.log('Standard format not found, trying UTF-8');
          // Try UTF-8 format
          filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
          if (filenameMatch) {
            filename = decodeURIComponent(filenameMatch[1]);
            console.log('UTF-8 filename extracted:', filename);
          } else {
            console.log('No filename found in headers');
          }
        } else {
          filename = filenameMatch[1];
          console.log('Standard filename extracted:', filename);
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Close dialog on success
      setExportDialogOpen(false);
    } catch (error: any) {
      console.error('Error exporting products:', error);
      setExportError(error.response?.data?.message || 'Error exporting products');
    } finally {
      setExportLoading(false);
    }
  };
  
  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  const handleDeleteClick = (product: any) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;

    setLoading(true);
    try {
      // Call the backend API to delete the product
      const response = await api.delete(`/products/${productToDelete.id}`);
      
      if (response.data && response.data.success) {
        // Update the state with the filtered products
        setProducts(products.filter(p => p.id !== productToDelete.id));
        setDeleteDialogOpen(false);
        setProductToDelete(null);
      } else {
        console.error('Error deleting product:', response.data);
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedCategory(newValue);
  };

  const filteredProducts = products
    .filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.sku.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 0 || product.category.id === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    // Sort products by ID in descending order (newest to oldest)
    .sort((a, b) => b.id - a.id);

  if (loading && products.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 3,
      maxWidth: '100vw',
      overflow: 'hidden',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isTablet ? 'flex-start' : 'center', 
        mb: 3,
        flexDirection: isTablet ? 'column' : 'row',
        gap: isTablet ? 2 : 0,
        width: '100%',
        maxWidth: '100%'
      }}>
        <Typography variant={isMobile ? "h5" : "h4"}>Products</Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          flexDirection: isTablet ? 'column' : 'row',
          width: isTablet ? '100%' : 'auto',
          maxWidth: '100%'
        }}>
          {hasPermission('products.import') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CloudUploadIcon />}
              onClick={handleImportClick}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isTablet}
              sx={{ 
                whiteSpace: 'nowrap',
                minWidth: isTablet ? 'auto' : '140px'
              }}
            >
              Import Products
            </Button>
          )}
          {hasPermission('products.read') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={stockCountLoading ? <CircularProgress size={18} /> : <PictureAsPdfIcon />}
              onClick={handleStockCountPDF}
              disabled={stockCountLoading}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isTablet}
              sx={{
                whiteSpace: 'nowrap',
                minWidth: isTablet ? 'auto' : '140px'
              }}
            >
              Stock Count List
            </Button>
          )}
          {hasPermission('products.export') && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isTablet}
              sx={{ 
                whiteSpace: 'nowrap',
                minWidth: isTablet ? 'auto' : '140px'
              }}
            >
              Export Products
            </Button>
          )}
          {(hasPermission('products.create') || hasPermission('products.create_limited')) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isTablet}
              sx={{ 
                whiteSpace: 'nowrap',
                minWidth: isTablet ? 'auto' : '120px'
              }}
            >
              Add Product
            </Button>
          )}
        </Box>
      </Box>

      {stockCountError && (
        <Alert severity="error" onClose={() => setStockCountError(null)} sx={{ mb: 2 }}>
          {stockCountError}
        </Alert>
      )}

      <Box sx={{
        mb: 3,
        width: '100%',
        maxWidth: '100%'
      }}>
        <TextField
          fullWidth
          placeholder="Search products by name or SKU"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          size="small"
          sx={{
            width: '100%',
            maxWidth: '100%',
            '& .MuiOutlinedInput-root': {
              width: '100%',
            }
          }}
        />
      </Box>

      <Paper sx={{ 
        mb: 3, 
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            width: '100%',
            maxWidth: '100%',
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
            '& .MuiTabs-scroller': {
              overflow: 'auto !important',
            },
            '& .MuiTabs-flexContainer': {
              width: '100%',
            }
          }}
        >
          {categories.map((category) => (
            <Tab 
              key={category.id} 
              label={category.name} 
              value={category.id}
              sx={{
                minWidth: isMobile ? 'auto' : isTablet ? 'auto' : 160,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                padding: isMobile ? '6px 8px' : isTablet ? '8px 12px' : '12px 16px',
                maxWidth: isTablet ? '150px' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {filteredProducts.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      ) : isMobile ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 2,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {filteredProducts.map((product) => (
            <Card 
              key={product.id}
              sx={{ 
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8],
                },
                border: '1px solid',
                borderColor: 'divider',
                maxWidth: '100%',
                width: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <CardMedia
                  component="img"
                  height={isMobile ? "180" : "200"}
                  image={getImageUrl(product.image)}
                  alt={product.name}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getImageUrl(null);
                  }}
                  onClick={() => handleImagePreview(getImageUrl(product.image), product.name)}
                  sx={{
                    cursor: 'pointer',
                    objectFit: 'cover',
                    transition: 'transform 0.3s ease',
                    width: '100%',
                    '&:hover': {
                      transform: 'scale(1.05)',
                    }
                  }}
                />
                {!isShipmentManager && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      borderRadius: '12px',
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color={product.stockCount < 10 ? 'error.main' : 'success.main'}
                      >
                        {product.stockCount} (B)
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color={product.stockSingle < 10 ? 'error.main' : 'success.main'}
                        sx={{ display: 'block' }}
                      >
                        {product.stockSingle} (S)
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              
              <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      component="h3" 
                      fontWeight="bold" 
                      sx={{ 
                        lineHeight: 1.2,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Chip
                      label={product.category.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{
                        fontSize: isMobile ? '0.6rem' : '0.75rem',
                        height: isMobile ? 20 : 24,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    SKU: {product.sku}
                  </Typography>
                  
                  {!isShipmentManager && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant={isMobile ? "h6" : "h5"} color="primary" fontWeight="bold">
                        {formatCurrency(typeof product.unitPrice === 'number' ? product.unitPrice : Number(product.unitPrice))}
                      </Typography>
                      {product.taxRate > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          +{product.taxRate}% tax
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2, flexWrap: 'wrap' }}>
                    {product.unit_size && (
                      <Box sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Unit Size
                        </Typography>
                        <Typography variant={isMobile ? "caption" : "body2"} fontWeight={500}>
                          {product.unit_size}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Qty in Box
                      </Typography>
                      <Typography variant={isMobile ? "caption" : "body2"} fontWeight={500}>
                        {product.qtyInBox}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: isMobile ? '60px' : 'auto' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Unit Type
                      </Typography>
                      <Typography variant={isMobile ? "caption" : "body2"} fontWeight={500}>
                        {product.unitType}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
              
              <CardActions sx={{ pt: 0, px: 2, pb: 2, justifyContent: 'space-between' }}>
                {hasPermission('products.update') && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditProduct(product)}
                    sx={{ borderRadius: '20px' }}
                  >
                    Edit
                  </Button>
                )}
                <Box>
                  {hasPermission('products.delete') && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(product)}
                      sx={{ 
                        backgroundColor: 'error.light',
                        color: 'error.contrastText',
                        '&:hover': {
                          backgroundColor: 'error.main',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, product)}
                    sx={{ ml: 1 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardActions>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer 
          component={Paper}
          sx={{
            width: '100%',
            maxWidth: '100%',
            overflow: 'auto'
          }}
        >
          <Table sx={{ 
            minWidth: 650,
            width: '100%'
          }}>
            <TableHead>
              <TableRow>
                <TableCell width="50">#</TableCell>
                <TableCell>Image</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Name</TableCell>
                <TableCell width="100">Category</TableCell>
                <TableCell align="right">Unit Size</TableCell>
                <TableCell align="right">Qty in Box</TableCell>
                <TableCell>Unit Type</TableCell>
                {!isShipmentManager && <TableCell align="right">Unit Price</TableCell>}
                {!isShipmentManager && <TableCell align="right">Tax Rate</TableCell>}
                {!isShipmentManager && <TableCell align="right">Stock</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product, index) => (
                <TableRow key={product.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={getImageUrl(product.image)}
                      alt={product.name}
                      onError={(e) => {
                        // If image fails to load, replace with placeholder
                        (e.target as HTMLImageElement).src = getImageUrl(null);
                      }}
                      onClick={() => handleImagePreview(getImageUrl(product.image), product.name)}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{product.sku}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.category.name}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{product.unit_size}</TableCell>
                  <TableCell align="right">{product.qtyInBox}</TableCell>
                  <TableCell>{product.unitType}</TableCell>
                  {!isShipmentManager && (
                    <TableCell align="right">{formatCurrency(typeof product.unitPrice === 'number' ? product.unitPrice : Number(product.unitPrice))}</TableCell>
                  )}
                  {!isShipmentManager && (
                    <TableCell align="right">{product.taxRate}%</TableCell>
                  )}
                  {!isShipmentManager && (
                    <TableCell align="right">
                      <Box>
                        <Typography
                          variant="body2"
                          color={product.stockCount < 10 ? 'error.main' : 'success.main'}
                        >
                          {product.stockCount} (B)
                        </Typography>
                        <Typography
                          variant="body2"
                          color={product.stockSingle < 10 ? 'error.main' : 'success.main'}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {product.stockSingle} (S)
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {hasPermission('products.update') && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditProduct(product)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {hasPermission('products.delete') && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(product)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, product)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Product actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {hasPermission('products.update') && (
          <MenuItem onClick={() => {
            handleMenuClose();
            if (selectedProduct) handleEditProduct(selectedProduct);
          }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {hasPermission('products.delete') && (
          <MenuItem onClick={() => {
            handleMenuClose();
            if (selectedProduct) handleDeleteClick(selectedProduct);
          }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Import products dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Products</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <DialogContentText sx={{ mb: 2, textAlign: 'center' }}>
              Upload a CSV or Excel file with product data
            </DialogContentText>
            
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center', mb: 1 }}>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<FileUploadIcon />}
                onClick={handleBrowseClick}
                disabled={importLoading}
                size="large"
              >
                Browse Files
              </Button>
              <Typography variant="body1" sx={{ ml: 2 }}>
                {importFile ? importFile.name : 'No file selected'}
              </Typography>
            </Box>
            
            <Link href="/api/products/template" download="product_import_template.csv" sx={{ mt: 1 }}>
              Download template file
            </Link>
          </Box>

          {/* Import Mode Selection */}
          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                Import Mode
              </FormLabel>
              <RadioGroup
                value={importMode}
                onChange={(e) => setImportMode(e.target.value as 'insert_only' | 'update_only' | 'upsert')}
                sx={{ pl: 1 }}
              >
                <FormControlLabel
                  value="upsert"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Insert and Update (Default)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add new products and update existing ones automatically.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="insert_only"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Insert Only
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add new products only. Skip existing SKUs.
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="update_only"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="medium">
                        Update Only
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Update existing products only. Skip new SKUs.
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {/* Collapsible Column Instructions */}
          <Box sx={{ mt: 2 }}>
            <Button 
              onClick={() => setShowColumnInstructions(!showColumnInstructions)} 
              variant="text" 
              size="small"
              sx={{ textTransform: 'none', pl: 0 }}
            >
              {showColumnInstructions ? '▼ Hide column instructions' : '▶ Show column instructions'}
            </Button>
            
            {showColumnInstructions && (
              <List dense sx={{ bgcolor: 'background.paper', mt: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <ListItem>
                  <ListItemText primary="sku" secondary="Required, must be unique" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="name" secondary="Required" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="description" secondary="Optional" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="category" secondary="Optional, will be created if not exists" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="unit_size" secondary="Optional" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="qty_in_box" secondary="Optional" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="unit_type" secondary="Optional, will be created if not exists" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="cost_price" secondary="Optional, defaults to 0" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="unit_price" secondary="Required" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="tax_rate" secondary="Optional, defaults to 0" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="stock_box" secondary="Optional, defaults to 0" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="stock_single" secondary="Optional, defaults to 0" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="reorder_level" secondary="Optional, defaults to 10" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="image_url" secondary="Optional" />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="is_active" secondary="Optional, defaults to true" />
                </ListItem>
              </List>
            )}
          </Box>
          
          {importError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importError}
            </Alert>
          )}
          
          {importResult && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success">
                <AlertTitle>Import Completed{importResult.mode && importResult.mode !== 'insert_only' ? ` (${importResult.mode === 'update_only' ? 'Update Only' : importResult.mode === 'upsert' ? 'Insert/Update' : 'Insert Only'} Mode)` : ''}</AlertTitle>
                <Typography variant="body2">
                  Total rows: {importResult.total}<br />
                  {/* Show legacy format for insert_only mode or backward compatibility */}
                  {(!importResult.mode || importResult.mode === 'insert_only') ? (
                    <>
                      Successfully imported: {importResult.successful || 0}<br />
                      Failed: {importResult.failed || 0}
                    </>
                  ) : (
                    <>
                      {/* Show detailed statistics for new modes */}
                      {importResult.inserted > 0 && <span>Inserted: {importResult.inserted}<br /></span>}
                      {importResult.updated > 0 && <span>Updated: {importResult.updated}<br /></span>}
                      {importResult.failed > 0 && <span>Failed: {importResult.failed}<br /></span>}
                      {importResult.inserted === 0 && importResult.updated === 0 && importResult.failed === 0 && (
                        <span>No changes made<br /></span>
                      )}
                    </>
                  )}
                </Typography>
                
                {importResult.newCategories.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">New categories created:</Typography>
                    <Typography variant="body2">
                      {importResult.newCategories.join(', ')}
                    </Typography>
                  </Box>
                )}
                
                {importResult.newUnitTypes.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">New unit types created:</Typography>
                    <Typography variant="body2">
                      {importResult.newUnitTypes.join(', ')}
                    </Typography>
                  </Box>
                )}
                
                {importResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="error">
                      Errors ({importResult.errors.length}):
                    </Typography>
                    <List dense>
                      {importResult.errors.slice(0, 5).map((error: any, index: number) => (
                        <ListItem key={index}>
                          <ListItemText 
                            primary={`Row ${error.row}: ${error.sku} - ${error.name}`}
                            secondary={error.error}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {importResult.errors.length > 5 && (
                      <Typography variant="body2" color="text.secondary">
                        ... and {importResult.errors.length - 5} more errors
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog} color="inherit">
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button 
              onClick={handleImportSubmit} 
              color="primary" 
              variant="contained"
              disabled={!importFile || importLoading}
            >
              {importLoading ? <CircularProgress size={24} /> : 'Import'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Export products dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Export Products</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Download all products from the system in your preferred format.
            {selectedCategory > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Only products from the selected category will be exported.
              </Typography>
            )}
          </DialogContentText>
          
          <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
            <FormLabel component="legend">Export Format</FormLabel>
            <RadioGroup
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'csv' | 'xlsx')}
              row
            >
              <FormControlLabel
                value="xlsx"
                control={<Radio />}
                label="Excel (Default)"
              />
              <FormControlLabel
                value="csv"
                control={<Radio />}
                label="CSV (Comma Separated Values)"
              />
            </RadioGroup>
          </FormControl>

          {/* Export Options */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%', mt: 2 }}>
            <FormLabel component="legend">Export Options</FormLabel>
            <RadioGroup
              value={exportMode}
              onChange={(e) => {
                setExportMode(e.target.value as 'customer' | 'advanced');
                if (e.target.value === 'customer') {
                  setShowAdvancedFilters(false);
                }
              }}
              row
            >
              <FormControlLabel
                value="customer"
                control={<Radio size="small" />}
                label="Customer Products"
              />
              <FormControlLabel
                value="advanced"
                control={<Radio size="small" />}
                label="Advanced Filters"
              />
            </RadioGroup>
          </FormControl>

          {/* Advanced Filters Section */}
          {exportMode === 'advanced' && (
            <Accordion expanded={showAdvancedFilters} onChange={() => setShowAdvancedFilters(!showAdvancedFilters)} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" fontWeight="medium">
                Advanced Filters
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                
                {/* Stock Count Filters */}
                <Typography variant="subtitle2" gutterBottom>
                  Stock Count Filters
                </Typography>
                
                {/* Box Stock Filter */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                      <FormLabel sx={{ mb: 1 }}>Box Stock</FormLabel>
                      <Select
                        value={exportFilters.stockBoxOperation}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, stockBoxOperation: e.target.value as any }))}
                      >
                        <SelectMenuItem value="all">All</SelectMenuItem>
                        <SelectMenuItem value="less_than">Less than</SelectMenuItem>
                        <SelectMenuItem value="greater_than">Greater than</SelectMenuItem>
                        <SelectMenuItem value="equal_to">Equal to</SelectMenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value"
                      type="number"
                      value={exportFilters.stockBoxValue}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, stockBoxValue: parseInt(e.target.value) || 0 }))}
                      disabled={exportFilters.stockBoxOperation === 'all'}
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                </Box>
                
                {/* Single Stock Filter */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth size="small">
                      <FormLabel sx={{ mb: 1 }}>Single Stock</FormLabel>
                      <Select
                        value={exportFilters.stockSingleOperation}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, stockSingleOperation: e.target.value as any }))}
                      >
                        <SelectMenuItem value="all">All</SelectMenuItem>
                        <SelectMenuItem value="less_than">Less than</SelectMenuItem>
                        <SelectMenuItem value="greater_than">Greater than</SelectMenuItem>
                        <SelectMenuItem value="equal_to">Equal to</SelectMenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Value"
                      type="number"
                      value={exportFilters.stockSingleValue}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, stockSingleValue: parseInt(e.target.value) || 0 }))}
                      disabled={exportFilters.stockSingleOperation === 'all'}
                      inputProps={{ min: 0 }}
                    />
                  </Box>
                </Box>
                
                {/* Price Range Filter */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Price Range
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Min Price"
                    type="number"
                    value={exportFilters.priceMin}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, priceMin: e.target.value }))}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="Max Price"
                    type="number"
                    value={exportFilters.priceMax}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, priceMax: e.target.value }))}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Box>
                
                {/* Active Status Filter */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Product Status
                </Typography>
                
                <Box sx={{ width: '50%' }}>
                  <FormControl fullWidth size="small">
                    <FormLabel sx={{ mb: 1 }}>Active Status</FormLabel>
                    <Select
                      value={exportFilters.activeStatus}
                      onChange={(e) => setExportFilters(prev => ({ ...prev, activeStatus: e.target.value as any }))}
                    >
                      <SelectMenuItem value="all">All Products</SelectMenuItem>
                      <SelectMenuItem value="active">Active Only</SelectMenuItem>
                      <SelectMenuItem value="inactive">Inactive Only</SelectMenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Date Range Filter */}
                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Date Range (Created Date)
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="From Date"
                    type="date"
                    value={exportFilters.dateFrom}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    label="To Date"
                    type="date"
                    value={exportFilters.dateTo}
                    onChange={(e) => setExportFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
                
                {/* Clear Filters Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setExportFilters({
                    stockBoxOperation: 'all',
                    stockBoxValue: 0,
                    stockSingleOperation: 'all',
                    stockSingleValue: 0,
                    priceMin: '',
                    priceMax: '',
                    activeStatus: 'all',
                    dateFrom: '',
                    dateTo: ''
                  })}
                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                >
                  Clear All Filters
                </Button>
                
              </Stack>
            </AccordionDetails>
          </Accordion>
          )}

          {exportError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {exportError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExportDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleExportSubmit} 
            color="primary" 
            variant="contained"
            disabled={exportLoading}
            startIcon={exportLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
          >
            {exportLoading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the product "{productToDelete?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', p: 2 }}>
            <Typography variant="h6" sx={{ color: 'white', flexGrow: 1 }}>
              {previewImage?.name}
            </Typography>
            <IconButton 
              onClick={handleCloseImagePreview}
              sx={{ color: 'white' }}
              size="large"
            >
              <ClearIcon />
            </IconButton>
          </Box>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.name}
              onError={(e) => {
                (e.target as HTMLImageElement).src = getImageUrl(null);
              }}
              sx={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1,
                cursor: 'pointer'
              }}
              onClick={handleCloseImagePreview}
            />
          )}
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              textAlign: 'center', 
              p: 2,
              fontSize: '0.875rem'
            }}
          >
            Tap image or close button to exit
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductList;
